/**
 * EPIFormWizard Component
 * Multi-step form wizard for creating/editing EPIs
 *
 * @version 2025.2.0
 */

import React, { useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Check as SaveIcon,
  SkipNext as SkipIcon,
} from '@mui/icons-material';
import { EpiFormData, EpiStatus, TipoDeRisco, Epi } from '../../../../types/epi';
import { centsToReais, reaisToCents } from '../../../../utils/financialHelpers';
import { EPIFormWizardProps } from './EPIFormWizard.types';
import { useEPIFormWizard } from './hooks/useEPIFormWizard';
import { IdentificationStep, CertificateStep, StockStep } from './steps';

/**
 * Get default form values from initial data
 */
const getDefaultValues = (
  initialData?: Partial<Epi>,
): EpiFormData => {
  if (initialData) {
    return {
      nome: initialData.nome || '',
      tipoDeRisco: initialData.tipoDeRisco || TipoDeRisco.FISICO,
      risco: initialData.risco || '',
      ativo: initialData.ativo || EpiStatus.ATIVO,
      metodo: initialData.metodo || '',
      inicioValidade: initialData.inicioValidade || undefined,
      fimValidade: initialData.fimValidade || '',
      atenuacao: initialData.atenuacao || '',
      efficiency: initialData.efficiency || undefined,
      brandModel: initialData.brandModel || '',
      eSocial: initialData.eSocial || '',
      quantidade: initialData.quantidade ?? undefined,
      unidade: initialData.unidade || '',
      tamanho: initialData.tamanho || '',
      custo: centsToReais(initialData.custo),
      observacao: initialData.observacao || '',
    };
  }

  return {
    nome: '',
    tipoDeRisco: TipoDeRisco.FISICO,
    risco: '',
    ativo: EpiStatus.ATIVO,
    metodo: '',
    inicioValidade: undefined,
    fimValidade: '',
    atenuacao: '',
    efficiency: undefined,
    brandModel: '',
    eSocial: '',
    quantidade: undefined,
    unidade: '',
    tamanho: '',
    custo: undefined,
    observacao: '',
  };
};

/**
 * Wizard content component (uses form context)
 */
const EPIFormWizardContent: React.FC<{
  onCancel: () => void;
  isSubmitting?: boolean;
}> = ({ onCancel, isSubmitting = false }) => {
  const {
    state,
    steps,
    isFirstStep,
    isLastStep,
    currentStepKey,
    canSkip,
    handleNext,
    handleBack,
    handleSkip,
  } = useEPIFormWizard();

  const onFormSubmit = async () => {
    const isValid = await handleNext();
    if (isValid && isLastStep) {
      // Trigger form submission by submitting the form element
      const form = document.querySelector('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
  };

  // Render current step
  const renderStep = () => {
    const disabled = state.loading || isSubmitting;

    switch (currentStepKey) {
      case 'identification':
        return <IdentificationStep disabled={disabled} />;
      case 'certificate':
        return <CertificateStep disabled={disabled} />;
      case 'stock':
        return <StockStep disabled={disabled} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={state.activeStep} sx={{ mb: 4 }}>
        {steps.map((step) => (
          <Step key={step.key}>
            <StepLabel optional={step.optional ? 'Opcional' : undefined}>
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error Alert */}
      {state.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {state.error}
        </Alert>
      )}

      {/* Current Step Content */}
      <Box sx={{ minHeight: 300, mb: 3 }}>
        {renderStep()}
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={state.loading || isSubmitting}
          >
            Cancelar
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isFirstStep && (
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
              disabled={state.loading || isSubmitting}
            >
              Voltar
            </Button>
          )}

          {canSkip && !isLastStep && (
            <Button
              variant="text"
              startIcon={<SkipIcon />}
              onClick={handleSkip}
              disabled={state.loading || isSubmitting}
            >
              Pular
            </Button>
          )}

          {isLastStep ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={onFormSubmit}
              disabled={state.loading || isSubmitting}
            >
              {isSubmitting ? 'Salvando...' : 'Salvar EPI'}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={state.loading ? <CircularProgress size={20} color="inherit" /> : <NextIcon />}
              onClick={handleNext}
              disabled={state.loading || isSubmitting}
            >
              Próximo
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Main EPIFormWizard component
 */
export const EPIFormWizard: React.FC<EPIFormWizardProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const defaultValues = useMemo(
    () => getDefaultValues(initialData),
    [initialData]
  );

  const methods = useForm<EpiFormData>({
    defaultValues,
    mode: 'onBlur',
  });

  const handleFormSubmit = async (data: EpiFormData) => {
    // Convert custo from reais to centavos if provided
    const submissionData = {
      ...data,
      custo: reaisToCents(data.custo),
    };
    await onSubmit(submissionData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <EPIFormWizardContent
          onCancel={onCancel}
          isSubmitting={isSubmitting}
        />
      </form>
    </FormProvider>
  );
};

export default EPIFormWizard;
